// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useRef, useMemo } from 'react'
import {
  Button,
  Alert,
  AlertDescription,
  AlertTitle,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { RefreshCw, AlertCircle, Play, CheckCircle2, Clock, XCircle, HelpCircle, FileText, Copy, Check, Archive, ChevronDown, ChevronRight, Database, Hash, Undo2 } from 'lucide-react'
import { listMigrations, runMigration, runMigrationBatch, rollbackMigration, getMigrationLog } from '../api/migrations'
import type { MigrationInfo } from '../api/types/migrations'

// Poll interval when a migration is running (ms)
const POLL_INTERVAL = 3000

function DeveloperGuideDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <HelpCircle className="h-4 w-4" />
          Dev Guide
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Migration Development Guide</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 text-sm">
          {/* Step 1 */}
          <section>
            <h3 className="font-semibold text-foreground mb-2">1. Create Migration</h3>
            <p className="text-muted-foreground mb-2">
              Add a Python file to <code className="bg-muted px-1 rounded">subsystems/migrations/</code>
            </p>
            <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
              m_YYYY_MM_DD_description.py
            </pre>
            <p className="text-muted-foreground mt-2">
              Auto-discovered. Appears here after deploy.
            </p>
          </section>

          {/* Step 2 */}
          <section>
            <h3 className="font-semibold text-foreground mb-2">2. Export Production Data</h3>
            <p className="text-muted-foreground mb-2">
              Export from PROD via <strong>Administration → Data Restore → Export</strong>, then download the snapshot.
              Upload to the shared base images bucket:
            </p>
            <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
{`aws s3 cp ~/Downloads/<snapshot>.zip s3://docreview-base-images/<snapshot>.zip`}
            </pre>
            <p className="text-muted-foreground mt-2">
              Update <code className="bg-muted px-1 rounded">migration.yml</code> with the base image name.
              Delete the local file after uploading.
            </p>
          </section>

          {/* Step 3 */}
          <section>
            <h3 className="font-semibold text-foreground mb-2">3. Restore to Sandbox</h3>
            <p className="text-muted-foreground mb-2">
              Upload the prod zip via <strong>Administration → Data Restore</strong>, or use CLI:
            </p>
            <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
              ./import.sh
            </pre>
            <p className="text-muted-foreground mt-2">
              Warning: Destructive — deletes all existing data first.
            </p>
          </section>

          {/* Step 4 */}
          <section>
            <h3 className="font-semibold text-foreground mb-2">4. Iterate</h3>
            <p className="text-muted-foreground">
              Run migration, verify results, fix issues. Reload clean prod data from zip as needed.
            </p>
          </section>

          {/* Step 5 */}
          <section>
            <h3 className="font-semibold text-foreground mb-2">5. Test in Dev & QA</h3>
            <p className="text-muted-foreground">
              Upload prod zip to <strong>Dev → Data Restore</strong>, run migration. Repeat for QA.
            </p>
          </section>

          {/* Step 6 */}
          <section>
            <h3 className="font-semibold text-foreground mb-2">6. Deploy to Production</h3>
            <p className="text-muted-foreground">
              Deploy code to prod, run migration. You've already verified it works against prod data.
            </p>
          </section>

          {/* Requirements */}
          <section className="border-t pt-4">
            <h3 className="font-semibold text-foreground mb-2">Requirements</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li><strong>Idempotent.</strong> Safe to run multiple times.</li>
              <li><strong>Logged.</strong> Use LOG_PREFIX for traceability.</li>
              <li><strong>Returns stats.</strong> Dict with custom keys (shown on card after run).</li>
              <li><strong>Reversible.</strong> If the migration mutates environment-coupled App rows or S3 via <code className="bg-muted px-1 rounded">MigrationOps</code>, set <code className="bg-muted px-1 rounded">reversible: true</code> in <code className="bg-muted px-1 rounded">migration.yml</code> to enable the Rollback button.</li>
            </ul>
          </section>

          {/* Docs link */}
          <section className="border-t pt-4">
            <h3 className="font-semibold text-foreground mb-2">Full Documentation</h3>
            <p className="text-muted-foreground">
              Run <code className="bg-muted px-1 rounded">./scripts/docs/docs.sh</code> and see{' '}
              <strong>Developer → Migrations</strong>.
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function StatusBadge({ status }: { status: MigrationInfo['status'] }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      )
    case 'running':
      return (
        <Badge variant="default" className="gap-1 bg-blue-600">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Running
        </Badge>
      )
    case 'completed':
      return (
        <Badge variant="default" className="gap-1 bg-green-600">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </Badge>
      )
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Error
        </Badge>
      )
  }
}

function MigrationDescription({ description }: { description: string }) {
  const lines = description.trim().split('\n').map(l => l.trim()).filter(Boolean)
  const hasBullets = lines.some(l => l.startsWith('- '))

  if (!hasBullets) {
    return (
      <p className="text-sm text-muted-foreground mb-3">{description.trim()}</p>
    )
  }

  return (
    <ul className="text-sm text-muted-foreground mb-3 list-disc list-inside space-y-0.5">
      {lines.map((line, i) => (
        <li key={i}>{line.replace(/^- /, '')}</li>
      ))}
    </ul>
  )
}

export function MigrationsManager() {
  const [migrations, setMigrations] = useState<MigrationInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Run confirmation dialog (single)
  const [runDialogOpen, setRunDialogOpen] = useState(false)
  const [migrationToRun, setMigrationToRun] = useState<MigrationInfo | null>(null)
  const [isRunning, setIsRunning] = useState(false)

  // Rollback confirmation dialog
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false)
  const [migrationToRollback, setMigrationToRollback] = useState<MigrationInfo | null>(null)
  const [isRollingBack, setIsRollingBack] = useState(false)

  // Batch run confirmation dialog
  const [batchDialogOpen, setBatchDialogOpen] = useState(false)
  const [isBatchRunning, setIsBatchRunning] = useState(false)

  // Log view dialog
  const [logDialogOpen, setLogDialogOpen] = useState(false)
  const [logContent, setLogContent] = useState<string>('')
  const [logMigrationId, setLogMigrationId] = useState<string>('')
  const [isLoadingLog, setIsLoadingLog] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  // Selection & filtering — selections are scoped to the active tab
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState('all')

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSelectedIds(new Set())
  }

  // Polling state
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Load migrations
  const loadMigrations = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setIsRefreshing(true)
      } else {
        setIsLoading(true)
      }
      setError(null)
      const data = await listMigrations()
      setMigrations(data)
    } catch (err) {
      console.error('[MigrationsManager] Failed to load migrations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load migrations')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  // Split active vs archived
  const activeMigrations = migrations.filter(m => !m.isArchived)
  const archivedMigrations = migrations.filter(m => m.isArchived)
  const [showHistory, setShowHistory] = useState(false)

  // Derive unique groups from active migrations
  const allGroups = useMemo(() => {
    const groups = new Set<string>()
    for (const m of activeMigrations) {
      for (const g of m.groups) {
        groups.add(g)
      }
    }
    return Array.from(groups).sort()
  }, [activeMigrations])

  // Filter active migrations by selected tab
  const filteredMigrations = useMemo(() => {
    if (activeTab === 'all') return activeMigrations
    return activeMigrations.filter(m => m.groups.includes(activeTab))
  }, [activeMigrations, activeTab])

  // Count active migrations per group (for tab labels)
  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const g of allGroups) {
      counts[g] = activeMigrations.filter(m => m.groups.includes(g)).length
    }
    return counts
  }, [activeMigrations, allGroups])

  // Check if any migration is running
  const hasRunningMigration = migrations.some(m => m.status === 'running')

  // Select-all for current filtered view
  const filteredIds = useMemo(() => new Set(filteredMigrations.map(m => m.id)), [filteredMigrations])
  const allFilteredSelected = filteredMigrations.length > 0 && filteredMigrations.every(m => selectedIds.has(m.id))

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      for (const id of filteredIds) {
        if (checked) {
          next.add(id)
        } else {
          next.delete(id)
        }
      }
      return next
    })
  }

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Start/stop polling based on running state
  useEffect(() => {
    if (hasRunningMigration && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(() => {
        loadMigrations(true)
      }, POLL_INTERVAL)
    } else if (!hasRunningMigration && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [hasRunningMigration])

  // Initial load
  useEffect(() => {
    loadMigrations()
  }, [])

  // Handle run single migration
  const handleRunClick = (migration: MigrationInfo) => {
    setMigrationToRun(migration)
    setRunDialogOpen(true)
  }

  const handleRunConfirm = async () => {
    if (!migrationToRun) return

    setIsRunning(true)
    try {
      await runMigration(migrationToRun.id)
      await loadMigrations(true)
      setRunDialogOpen(false)
      setMigrationToRun(null)
    } catch (err) {
      console.error('[MigrationsManager] Failed to run migration:', err)
      setError(err instanceof Error ? err.message : 'Failed to run migration')
    } finally {
      setIsRunning(false)
    }
  }

  // Handle rollback
  const handleRollbackClick = (migration: MigrationInfo) => {
    setMigrationToRollback(migration)
    setRollbackDialogOpen(true)
  }

  const handleRollbackConfirm = async () => {
    if (!migrationToRollback) return

    setIsRollingBack(true)
    try {
      await rollbackMigration(migrationToRollback.id)
      await loadMigrations(true)
      setRollbackDialogOpen(false)
      setMigrationToRollback(null)
    } catch (err) {
      console.error('[MigrationsManager] Failed to roll back migration:', err)
      setError(err instanceof Error ? err.message : 'Failed to roll back migration')
    } finally {
      setIsRollingBack(false)
    }
  }

  // Handle batch run
  const handleBatchRunConfirm = async () => {
    setIsBatchRunning(true)
    try {
      await runMigrationBatch(Array.from(selectedIds))
      setSelectedIds(new Set())
      await loadMigrations(true)
      setBatchDialogOpen(false)
    } catch (err) {
      console.error('[MigrationsManager] Failed to run batch:', err)
      setError(err instanceof Error ? err.message : 'Failed to run batch migration')
    } finally {
      setIsBatchRunning(false)
    }
  }

  // Handle view log
  const handleViewLog = async (migration: MigrationInfo) => {
    setLogMigrationId(migration.id)
    setLogDialogOpen(true)
    setIsLoadingLog(true)
    setIsCopied(false)
    try {
      const content = await getMigrationLog(migration.id)
      setLogContent(content)
    } catch (err) {
      console.error('[MigrationsManager] Failed to load log:', err)
      setLogContent('Failed to load log')
    } finally {
      setIsLoadingLog(false)
    }
  }

  // Handle copy to clipboard
  const handleCopyLog = async () => {
    try {
      await navigator.clipboard.writeText(logContent)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('[MigrationsManager] Failed to copy:', err)
    }
  }

  // Format date for display
  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Never'
    return new Date(isoString).toLocaleString()
  }

  // Selected IDs in backend execution order (preserves migrations array order)
  const sortedSelectedIds = migrations.filter(m => selectedIds.has(m.id)).map(m => m.id)

  return (
    <div className="text-slate-900">
      <div className="space-y-5">
        {/* Header Section */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1
              className="text-[21px] font-[650] tracking-[-0.015em]"
              data-testid="migrations-page-heading"
            >
              Data Migrations
            </h1>
            <p className="mt-[5px] text-[13px] text-slate-500">
              One-off scripts that upgrade stored data after a schema change or fix bad records.
              Each runs on demand, reports its counts, and records when it last ran.
            </p>
          </div>
          <div className="flex gap-2">
            <DeveloperGuideDialog />
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadMigrations(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
            <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
            <span className="text-[13px] text-slate-500">Loading migrations…</span>
          </div>
        )}

        {/* Migrations List */}
        {!isLoading && migrations.length === 0 && (
          <div className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 text-center shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
            <div className="text-[13.5px] font-[550] text-slate-700">No migrations found</div>
            <div className="mt-1 text-[12.5px] text-slate-400">
              Migrations ship with releases and appear here when pending.
            </div>
          </div>
        )}

        {/* Active Migrations with Group Tabs */}
        {!isLoading && activeMigrations.length > 0 && (
          <div className="space-y-4">
            {/* Filter tabs + batch controls */}
            <div className="flex items-center justify-between gap-4">
              {allGroups.length > 0 && (
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                  <TabsList>
                    <TabsTrigger value="all">All ({activeMigrations.length})</TabsTrigger>
                    {allGroups.map(group => (
                      <TabsTrigger key={group} value={group}>
                        {group} ({groupCounts[group]})
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              )}

              <div className="flex items-center gap-3 ml-auto">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <Checkbox
                    checked={allFilteredSelected}
                    onCheckedChange={(checked) => handleSelectAll(checked === true)}
                    aria-label="Select all migrations"
                  />
                  Select all
                </label>
                {selectedIds.size > 0 && (
                  <Button
                    size="sm"
                    onClick={() => setBatchDialogOpen(true)}
                    disabled={hasRunningMigration}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Run Selected ({selectedIds.size})
                  </Button>
                )}
              </div>
            </div>

            {/* Migration Cards */}
            <div className="grid gap-4">
              {filteredMigrations.map(migration => (
                <Card
                  key={migration.id}
                  className="overflow-hidden rounded-xl border-[#e7ecf2] shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
                >
                  <CardHeader className="pb-3">
                    {/* Row 1: Checkbox + Name + Status + Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedIds.has(migration.id)}
                          onCheckedChange={() => handleToggleSelect(migration.id)}
                          aria-label={`Select ${migration.name}`}
                        />
                        <CardTitle className="text-lg">{migration.name}</CardTitle>
                        <StatusBadge status={migration.status} />
                      </div>
                      <div className="flex gap-2">
                        {migration.logKey && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewLog(migration)}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            View Log
                          </Button>
                        )}
                        {migration.reversible && (migration.status === 'completed' || migration.status === 'error') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRollbackClick(migration)}
                            disabled={hasRunningMigration}
                            className="text-amber-700 hover:text-amber-800 dark:text-amber-500 dark:hover:text-amber-400"
                          >
                            <Undo2 className="h-4 w-4 mr-2" />
                            Rollback
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleRunClick(migration)}
                          disabled={false}
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Run
                        </Button>
                      </div>
                    </div>

                    {/* Row 2: ID + Hash + Group badges */}
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs text-muted-foreground">{migration.id}</code>
                      {migration.contentHash && (
                        <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                          <Hash className="h-3 w-3" />
                          {migration.contentHash}
                        </span>
                      )}
                      {migration.groups.length > 0 && (
                        <>
                          <span className="text-muted-foreground/40">|</span>
                          {migration.groups.map(group => (
                            <Badge key={group} variant="outline" className="text-[11px] px-1.5 py-0">
                              {group}
                            </Badge>
                          ))}
                        </>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    {migration.description && (
                      <MigrationDescription description={migration.description} />
                    )}

                    {/* Metadata row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        Last Run: <span className="text-foreground">{formatDate(migration.lastRunAt)}</span>
                      </span>

                      {migration.baseImage && (
                        <span className="inline-flex items-center gap-1">
                          <Database className="h-3 w-3" />
                          Base Image: <span className="font-mono text-foreground">{migration.baseImage}</span>
                        </span>
                      )}

                      {migration.stats && Object.entries(migration.stats).map(([key, value]) => (
                        <span key={key}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:{' '}
                          <span className={key === 'errors' && value > 0 ? 'text-destructive font-medium' : 'text-foreground'}>
                            {value}
                          </span>
                        </span>
                      ))}
                    </div>

                    {migration.error && (
                      <Alert variant="destructive" className="mt-3">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="font-mono text-xs">
                          {migration.error}
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Archived Migrations */}
        {!isLoading && archivedMigrations.length > 0 && (
          <div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
            >
              {showHistory ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Archive className="h-4 w-4" />
              History ({archivedMigrations.length})
            </button>

            {showHistory && (
              <div className="grid gap-3">
                {archivedMigrations.map(migration => (
                  <Card
                    key={migration.id}
                    className="rounded-xl border-[#e7ecf2] opacity-75 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-base">{migration.name}</CardTitle>
                          <StatusBadge status={migration.status} />
                        </div>
                        {migration.logKey && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewLog(migration)}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            View Log
                          </Button>
                        )}
                      </div>
                      <CardDescription className="font-mono text-xs">
                        {migration.id}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {migration.description && (
                        <MigrationDescription description={migration.description} />
                      )}

                      <div className="flex flex-wrap gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Last Run: </span>
                          <span>{formatDate(migration.lastRunAt)}</span>
                        </div>

                        {migration.stats && Object.entries(migration.stats).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-muted-foreground">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:{' '}
                            </span>
                            <span className={key === 'errors' && value > 0 ? 'text-destructive' : ''}>
                              {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Run Single Migration Confirmation Dialog */}
        <AlertDialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Run Migration</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to run the migration{' '}
                <span className="font-semibold font-mono">
                  {migrationToRun?.id}
                </span>
                ? This will execute on the jobs lambda and may take several minutes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isRunning}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRunConfirm}
                disabled={isRunning}
              >
                {isRunning ? 'Starting...' : 'Run Migration'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Rollback Confirmation Dialog */}
        <AlertDialog open={rollbackDialogOpen} onOpenChange={setRollbackDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Roll Back Migration</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p className="mb-3">
                    This restores the backed-up changes made by{' '}
                    <span className="font-semibold font-mono">{migrationToRollback?.id}</span>{' '}
                    and resets it to pending so it can be run again.
                  </p>
                  <p className="text-xs">
                    Only changes to App rows preserved by Data Restore and S3 objects are backed up
                    and undone. Restore-managed configuration is re-baselined by Data Restore instead.
                    Rollback is only possible within the backup retention window.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isRollingBack}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRollbackConfirm}
                disabled={isRollingBack}
              >
                {isRollingBack ? 'Starting...' : 'Roll Back'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Batch Run Confirmation Dialog */}
        <AlertDialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Run {selectedIds.size} Migrations</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p className="mb-3">
                    The following migrations will run sequentially in order. If any fails, remaining migrations will not run.
                  </p>
                  <ol className="list-decimal list-inside space-y-1 font-mono text-xs">
                    {sortedSelectedIds.map(id => (
                      <li key={id}>{id}</li>
                    ))}
                  </ol>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isBatchRunning}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBatchRunConfirm}
                disabled={isBatchRunning}
              >
                {isBatchRunning ? 'Starting...' : `Run ${selectedIds.size} Migrations`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Log View Dialog */}
        <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="font-mono text-sm pr-16">
                {logMigrationId}
              </DialogTitle>
            </DialogHeader>
            <div className="flex justify-end -mt-2 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLog}
                disabled={isLoadingLog}
                className="gap-2"
              >
                {isCopied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="flex-1 overflow-auto">
              {isLoadingLog ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading log...</span>
                </div>
              ) : (
                <pre className="bg-muted p-4 rounded text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                  {logContent}
                </pre>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
